import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { TRENDING_CARDS, TRENDING_FILTERS } from "@/components/landing/landing-data";

/**
 * "Explore what others are unfolding." Static, curated cards (the homepage never
 * calls the API). Everything that leads into the library goes to sign-in, since the
 * library needs an account: the cards, "Browse the library", and the category pills.
 * "All" is shown as the current view; the row always shows every card.
 */
export function TrendingSection({ loginUrl }: { loginUrl: string }) {
  const [edges, setEdges] = useState({ start: true, end: false });
  const trackRef = useRef<HTMLUListElement>(null);

  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setEdges({
      start: el.scrollLeft <= 4,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, [updateEdges]);

  const scrollByCard = (dir: 1 | -1) => {
    const el = trackRef.current;
    const card = el?.querySelector("li");
    if (!el || !card) return;
    el.scrollBy({ left: dir * (card.getBoundingClientRect().width + 18) });
  };

  return (
    <section id="explore" aria-labelledby="lp-explore-title" className="relative py-20 md:pb-16 md:pt-[110px]">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between md:gap-10">
          <div className="flex flex-col gap-4">
            <p className="lp-eyebrow">Real people</p>
            <h2 id="lp-explore-title" className="lp-h2">
              Explore what others are&nbsp;unfolding.
            </h2>
          </div>
          <a href={loginUrl} className="lp-btn-dark min-h-12 self-start px-[22px] text-base md:self-auto md:shrink-0">
            Browse the library <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        </div>

        <nav aria-label="Browse by category" className="mt-9">
          <ul className="flex flex-wrap gap-2.5">
            <li>
              <span className="lp-pill inline-flex items-center" aria-current="true">
                All
              </span>
            </li>
            {TRENDING_FILTERS.map((f) => (
              <li key={f}>
                <a href={loginUrl} className="lp-pill inline-flex items-center">
                  {f}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-10 flex items-center justify-between">
          <h3 className="text-[22px] font-semibold tracking-[-0.02em] text-(--lp-ink) sm:text-[26px]">Trending</h3>
          <div className="hidden gap-2.5 sm:flex">
            <button
              type="button"
              className="lp-arrow"
              aria-label="Previous"
              aria-controls="lp-trending"
              disabled={edges.start}
              onClick={() => scrollByCard(-1)}
            >
              <ChevronLeft className="h-[18px] w-[18px]" aria-hidden />
            </button>
            <button
              type="button"
              className="lp-arrow"
              aria-label="Next"
              aria-controls="lp-trending"
              disabled={edges.end}
              onClick={() => scrollByCard(1)}
            >
              <ChevronRight className="h-[18px] w-[18px]" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {/* The track runs to the right edge of the viewport; its left padding lines the first card up with the content column. */}
      <div className="mx-auto mt-5 max-w-[1200px] sm:[mask-image:linear-gradient(90deg,#000_88%,transparent)]">
        <ul
          id="lp-trending"
          ref={trackRef}
          onScroll={updateEdges}
          aria-label="Trending triggers"
          className="lp-track scroll-px-4 px-4 pb-4 sm:scroll-px-8 sm:px-8 lg:scroll-px-12 lg:px-12"
        >
          {TRENDING_CARDS.map((c) => (
            <li key={c.title}>
              <a href={loginUrl} className="lp-card">
                <span className="lp-card-media">
                  <img src={c.image} alt="" width={640} height={800} loading="lazy" decoding="async" />
                  <span className="lp-card-scrim" aria-hidden />
                  <span
                    aria-hidden
                    className="absolute left-[18px] top-[18px] flex h-11 w-11 items-center justify-center rounded-xl bg-white/25 backdrop-blur-sm"
                  >
                    <Play className="h-4 w-4 fill-white text-white" />
                  </span>
                  <span className="absolute right-4 top-[26px] rounded-full border border-white/15 bg-black/45 px-2.5 py-[5px] text-[10.5px] uppercase tracking-[0.14em] text-white">
                    {c.category}
                  </span>
                  <span aria-hidden className="lp-card-go absolute bottom-3 right-3.5">
                    <ArrowRight className="h-[15px] w-[15px]" />
                  </span>
                </span>
                <span className="lp-card-title px-1.5 pt-4 text-[17px] leading-[1.35] text-(--lp-ink) sm:text-[19px]">{c.title}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
